import * as TeamActions from '@/store/actions/Team.actions';
import * as TeamMutations from '@/store/mutations/Team.mutations';
import * as AuthMutations from '@/store/mutations/Auth.mutations';
import * as DatabaseService from '@/services/Database.service';
import { dictionaryFromDocumentArray, isArrayEmpty } from '@/helpers/dataHelpers';
import * as NotificationService from '@/services/Notification.service';
import * as notificationTypes from '@/constants/notificationTypes';

const getInitialState = () => ({
  teams: {},
  isTeamLoading: false,
});

const state = getInitialState();

const getters = {
  teamIds(state) {
    return Object.keys(state.teams).map(key => key);
  },
  workspaceTeams(state, getters, rootState) {
    if (getters.teamIds.length === 0) {
      return {};
    }

    return getters.teamIds.reduce((acc, teamId) => {
      if (state.teams && state.teams[teamId].workspaceId === rootState.workspace.activeWorkspaceId) {
        acc[teamId] = state.teams[teamId];
      }

      return acc;
    }, {});
  },
  loggedInUserTeams(state, getters, rootState) {
    if (!getters.teamIds || getters.teamIds.length === 0) {
      return {};
    }

    return getters.teamIds.reduce((acc, teamId) => {
      if (getters.workspaceTeams[teamId].userIds.includes(rootState.auth.loggedInUserId)) {
        acc[teamId] = getters.workspaceTeams[teamId];
      }

      return acc;
    }, {});
  },
  loggedInUserTeammateIds(state, getters) {
    if (!getters.loggedInUserTeams) {
      return [];
    }

    const userIdsInTeams = Object.keys(getters.loggedInUserTeams)
      .map(key => getters.loggedInUserTeams[key].userIds)
      .flat();

    return [...new Set(userIdsInTeams)]; // Remove duplicates.
  },
  doesLoggedInUserHaveTeammates(state, getters) {
    return !isArrayEmpty(getters.loggedInUserTeammateIds);
  },
};

const mutations = {
  [TeamMutations.TEAM_REQUEST_DATA](state) {
    state.isTeamLoading = true;
  },
  [TeamMutations.TEAM_BEGIN_WRITE_DATA](state) {
    state.isTeamLoading = true;
  },
  [TeamMutations.TEAM_COMPLETE_WRITE_DATA](state) {
    state.isTeamLoading = false;
  },
  [TeamMutations.RECEIVE_TEAMS](state, payload) {
    state.teams = {
      ...state.teams,
      ...payload,
    };
    state.isTeamLoading = false;
  },
  [AuthMutations.LOG_OUT](state) {
    Object.assign(state, getInitialState());
  },
};

const actions = {
  async [TeamActions.FETCH_WORKSPACE_TEAMS](context, payload) {
    context.commit(TeamMutations.TEAM_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const { docs: workspaceTeams } = await DatabaseService.getWorkspaceTeams(payload.workspaceId);
        const shapedWorkspaceTeams = dictionaryFromDocumentArray(workspaceTeams);

        context.commit(TeamMutations.RECEIVE_TEAMS, shapedWorkspaceTeams);
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject();
      }
    });
  },
  async [TeamActions.FETCH_PUBLIC_WORKSPACE_TEAMS](context, payload) {
    context.commit(TeamMutations.TEAM_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const { docs: workspaceTeams } = await DatabaseService.getPublicWorkspaceTeams(payload.workspaceId);
        const shapedWorkspaceTeams = dictionaryFromDocumentArray(workspaceTeams);

        context.commit(TeamMutations.RECEIVE_TEAMS, shapedWorkspaceTeams);
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject();
      }
    });
  },
  async [TeamActions.FETCH_TEAMS_BY_IDS](context, payload) {
    context.commit(TeamMutations.TEAM_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const teamDocuments = await Promise.all(DatabaseService.getTeamsByIds(payload.teamIds));
        const shapedTeams = dictionaryFromDocumentArray(teamDocuments);

        context.commit(TeamMutations.RECEIVE_TEAMS, shapedTeams);
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject();
      }
    });
  },
  async [TeamActions.FETCH_USER_TEAMS](context, payload) {
    context.commit(TeamMutations.TEAM_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const { docs: teamDocuments } = await DatabaseService.getTeamsByUserIdAndWorkspaceId(payload.userId, payload.workspaceId); // eslint-disable-line max-len
        const shapedTeams = dictionaryFromDocumentArray(teamDocuments);

        context.commit(TeamMutations.RECEIVE_TEAMS, shapedTeams);
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject();
      }
    });
  },
  async [TeamActions.FETCH_PUBLIC_USER_TEAMS](context, payload) {
    context.commit(TeamMutations.TEAM_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const { docs: teamDocuments } = await DatabaseService.getPublicTeamsByUserId(payload.userId, payload.workspaceId);
        const shapedTeams = dictionaryFromDocumentArray(teamDocuments);

        context.commit(TeamMutations.RECEIVE_TEAMS, shapedTeams);
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject();
      }
    });
  },
  async [TeamActions.CREATE_TEAM](context, payload) {
    context.commit(TeamMutations.TEAM_BEGIN_WRITE_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const createdTeamRef = await DatabaseService.addNewTeam(payload.newTeam);
        context.commit(TeamMutations.TEAM_COMPLETE_WRITE_DATA);

        await context.dispatch(TeamActions.FETCH_TEAMS_BY_IDS, { teamIds: [createdTeamRef.id] });
        resolve(createdTeamRef);
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
        // @TODO -- Set error mutation.
      }
    });
  },
  async [TeamActions.UPDATE_TEAM](context, payload) {
    context.commit(TeamMutations.TEAM_BEGIN_WRITE_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        await DatabaseService.updateTeam(payload.teamId, payload.updatedTeam);
        context.commit(TeamMutations.TEAM_COMPLETE_WRITE_DATA);

        await context.dispatch(TeamActions.FETCH_TEAMS_BY_IDS, { teamIds: [payload.teamId] });
        NotificationService.showNotification(notificationTypes.UPDATE_TEAM_SUCCESS);
        resolve();
      } catch (err) {
        NotificationService.showNotification(notificationTypes.UPDATE_TEAM_ERROR);
        console.error(err); // eslint-disable-line no-console
        reject(err);
        // @TODO -- Set error mutation.
      }
    });
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
